import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, GraduationCap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SERVICE_OPTIONS = [
  "Thesis Writing Assistance", "Dissertation Support", "Synopsis Writing",
  "Research Paper Support", "Assignment / Project", "Formatting Service",
  "Plagiarism Check", "Editing & Proofreading",
];
const CITATIONS = ["APA", "MLA", "IEEE", "Vancouver", "Bluebook", "Chicago", "Other"];

const INIT = {
  name: "", mobile: "", email: "", city: "", course: "", college: "",
  service_type: "Thesis Writing Assistance", topic: "", subject: "",
  word_count: "", language: "English", citation_style: "APA",
  deadline: "", plagiarism_requirement: "", need_printing: false, notes: "",
};

export default function AcademicRequest() {
  const [form, setForm] = useState(INIT);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderNo, setOrderNo] = useState(null);
  const { user } = useAuth();
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    if (!user) {
      toast.error("Login required for secure file upload. You can still submit without files.");
      return;
    }
    const selected = Array.from(e.target.files || []);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of selected) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      setFiles((p) => [...p, ...uploaded]);
      toast.success("Files uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, file_ids: files.map((f) => f.id) };
      const { data } = await api.post("/orders/academic", payload);
      setOrderNo(data.order_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderNo) {
    return (
      <div className="container-app py-24 text-center max-w-2xl mx-auto" data-testid="academic-success">
        <CheckCircle2 className="w-14 h-14 mx-auto text-gold" />
        <h1 className="font-display text-4xl mt-6 text-navy">Thank you</h1>
        <p className="text-slate-600 mt-3">Your academic service request has been received. Our team will review your requirement and contact you with a quotation.</p>
        <div className="mt-6 inline-flex items-center gap-2 border border-slate-200 rounded-sm px-4 py-3 font-mono">
          Request number: <span className="text-navy font-semibold" data-testid="academic-order-number">{orderNo}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="academic-page" className="container-app py-16">
      <span className="section-eyebrow">Academic services · Custom request</span>
      <div className="divider-gold mt-2 mb-4" />
      <h1 className="font-display text-4xl sm:text-5xl text-navy flex items-center gap-3">
        <GraduationCap className="w-9 h-9 text-gold" /> Request academic help
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Submit your topic, guidelines, and deadline. Our team will share a personalized quotation over email/WhatsApp.</p>

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Fieldset title="Your details">
            <Grid>
              <F label="Full name"><Input required value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="ac-name" /></F>
              <F label="Mobile number"><Input required value={form.mobile} onChange={(e) => set("mobile")(e.target.value)} data-testid="ac-mobile" /></F>
              <F label="Email"><Input type="email" required value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="ac-email" /></F>
              <F label="City"><Input value={form.city} onChange={(e) => set("city")(e.target.value)} /></F>
              <F label="Course / degree"><Input value={form.course} onChange={(e) => set("course")(e.target.value)} /></F>
              <F label="College / university"><Input value={form.college} onChange={(e) => set("college")(e.target.value)} /></F>
            </Grid>
          </Fieldset>

          <Fieldset title="Service requirement">
            <Grid>
              <F label="Service required"><SelectC value={form.service_type} onChange={set("service_type")} options={SERVICE_OPTIONS} testid="ac-service" /></F>
              <F label="Subject / department"><Input value={form.subject} onChange={(e) => set("subject")(e.target.value)} /></F>
              <F label="Topic / title" className="sm:col-span-2 md:col-span-3">
                <Input required value={form.topic} onChange={(e) => set("topic")(e.target.value)} data-testid="ac-topic" />
              </F>
              <F label="Word count / pages"><Input value={form.word_count} onChange={(e) => set("word_count")(e.target.value)} /></F>
              <F label="Language"><SelectC value={form.language} onChange={set("language")} options={["English", "Hindi"]} /></F>
              <F label="Citation style"><SelectC value={form.citation_style} onChange={set("citation_style")} options={CITATIONS} /></F>
              <F label="Deadline"><Input type="date" value={form.deadline} onChange={(e) => set("deadline")(e.target.value)} /></F>
              <F label="Plagiarism requirement (e.g. <10%)"><Input value={form.plagiarism_requirement} onChange={(e) => set("plagiarism_requirement")(e.target.value)} /></F>
            </Grid>
            <div className="mt-6 flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2 max-w-sm">
              <div className="text-sm text-slate-700">Need printing after completion?</div>
              <Switch data-testid="ac-need-printing" checked={form.need_printing} onCheckedChange={set("need_printing")} />
            </div>
          </Fieldset>

          <Fieldset title="Attachments (optional)">
            <p className="text-xs text-slate-500 mb-3">Upload university guidelines, sample papers, or any existing draft. {user ? "" : "Login required for secure upload."}</p>
            <input data-testid="ac-file-input" type="file" multiple onChange={onFile} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="text-sm" />
            {uploading && <div className="mt-3 text-sm text-royal flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</div>}
            {files.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {files.map((f) => <li key={f.id} className="font-mono text-xs">• {f.original_filename}</li>)}
              </ul>
            )}
          </Fieldset>

          <Fieldset title="Additional instructions">
            <Textarea rows={4} value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Anything specific we should know?" data-testid="ac-notes" />
          </Fieldset>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 border border-slate-200 rounded-sm bg-navy text-white p-6">
            <div className="section-eyebrow text-gold">Submit your request</div>
            <p className="text-xs text-slate-300 mt-2">Our team will reach out on WhatsApp/email with a customized quotation within 12 hours.</p>
            <button type="submit" disabled={submitting} className="btn-gold w-full mt-6" data-testid="academic-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Send request"}
            </button>
            <div className="mt-6 text-[11px] font-mono text-slate-400">
              We provide academic <strong className="text-gold">support</strong> — editing, formatting, citation correction, plagiarism review — not academic dishonesty.
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Fieldset = ({ title, children }) => (
  <div className="border border-slate-200 rounded-sm p-6 bg-white">
    <div className="section-eyebrow mb-4">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">{children}</div>;
const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
const SelectC = ({ value, onChange, options, testid }) => {
  const norm = options.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-10"><SelectValue /></SelectTrigger>
      <SelectContent>{norm.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}</SelectContent>
    </Select>
  );
};
